/**
 * AdminDeniedAttempts — every refusal the guards logged, grouped by person and
 * capability, each with the one grant that would clear it.
 *
 * Route: /admin/governance/denied-attempts  (super-admin only)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, ShieldAlert, Search, Check, Lock, KeyRound, Clock, Users, X, Inbox,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });
const WINDOWS = [7, 30, 90, 365];

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export default function AdminDeniedAttempts() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('refusals');
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const q = useQuery({
    queryKey: ['denied-attempts', days, search, includeDeleted],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/rbac/denied-attempts`,
      { params: { days, search, include_deleted: includeDeleted, limit: 200 }, headers: authH() },
    )).data,
  });

  const grant = useMutation({
    mutationFn: async ({ admin_id, capability }) => (await axios.post(
      `${API}/api/admin/rbac/denied-attempts/grant`,
      { admin_id, capability },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => {
      toast.success(`${d.granted} granted to ${d.email}`);
      qc.invalidateQueries({ queryKey: ['denied-attempts'] });
      qc.invalidateQueries({ queryKey: ['team-caps'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const items = q.data?.items || [];

  const reqQ = useQuery({
    queryKey: ['grant-requests'],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/rbac/grant-requests`, { params: { status: 'all' }, headers: authH() },
    )).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, verdict }) => (await axios.post(
      `${API}/api/admin/rbac/grant-requests/${id}/${verdict}`, {},
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => {
      toast.success(d.status === 'approved' ? `${d.capability} granted to ${d.email}` : `${d.capability} declined`);
      qc.invalidateQueries({ queryKey: ['grant-requests'] });
      qc.invalidateQueries({ queryKey: ['denied-attempts'] });
      qc.invalidateQueries({ queryKey: ['team-caps'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const requests = reqQ.data?.items || [];
  const pending = requests.filter((r) => r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="denied-attempts-page">
      <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
        <div>
          <Button
            variant="ghost" size="sm"
            className="mb-1 text-slate-400 hover:text-slate-100"
            onClick={() => navigate(-1)} data-testid="back-btn"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ShieldAlert className="h-6 w-6 text-amber-400" /> Denied attempts
          </h1>
          <p className="text-xs text-slate-500">
            Every refusal from both guards — the strict money/security check and the
            capability baseline — with the exact grant that would fix each one.
          </p>
        </div>

        <div className="flex gap-2 border-b border-slate-800">
          <button
            type="button"
            data-testid="tab-refusals"
            onClick={() => setTab('refusals')}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === 'refusals'
              ? 'border-amber-500 text-amber-200' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
          >
            Refusals
          </button>
          <button
            type="button"
            data-testid="tab-requests"
            onClick={() => setTab('requests')}
            className={`-mb-px flex items-center gap-1 border-b-2 px-3 py-2 text-sm ${tab === 'requests'
              ? 'border-emerald-500 text-emerald-200' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
          >
            <Inbox className="h-3.5 w-3.5" /> Requests
            {pending.length > 0 && (
              <Badge className="border-emerald-500/40 bg-emerald-500/20 text-[10px] text-emerald-200">
                {pending.length}
              </Badge>
            )}
          </button>
        </div>

        {tab === 'requests' ? (
          <Card className="border-slate-800 bg-slate-900/60" data-testid="requests-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Access requested</CardTitle>
              <p className="text-xs text-slate-500">
                Filed by the person the guard refused, carrying the exact capability it named.
                Approving applies the grant straight away.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {reqQ.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
              {!reqQ.isLoading && requests.length === 0 && (
                <p className="p-4 text-center text-xs text-slate-500" data-testid="requests-empty">
                  Nobody has asked for access yet.
                </p>
              )}
              {requests.map((r) => (
                <div
                  key={r.id}
                  data-testid={`request-row-${r.id}`}
                  className="flex flex-wrap items-start justify-between gap-3 rounded border border-slate-800 bg-slate-950 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-100">{r.email || r.admin_id}</span>
                      <Badge className="border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] text-emerald-200">
                        {r.capability}
                      </Badge>
                      <Badge className={`text-[10px] ${
                        r.status === 'pending' ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                          : r.status === 'approved' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                          : 'border-slate-600 bg-slate-800 text-slate-400'}`}
                      >
                        {r.status}
                      </Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                      <Clock className="h-3 w-3" /> asked {when(r.created_at)}
                      {r.decided_at && ` · decided ${when(r.decided_at)}${r.decided_by ? ` by ${r.decided_by}` : ''}`}
                    </div>
                    {r.path && (
                      <code className="mt-1 inline-block rounded bg-slate-900 px-1 py-0.5 text-[10px] text-slate-400">
                        {r.method} {r.path}
                      </code>
                    )}
                    {r.reason && <p className="mt-1 text-[11px] text-slate-400">“{r.reason}”</p>}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        data-testid={`approve-request-${r.id}`}
                        className="bg-emerald-600 hover:bg-emerald-500"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, verdict: 'approve' })}
                      >
                        <Check className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        data-testid={`decline-request-${r.id}`}
                        className="text-slate-400 hover:text-rose-300"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, verdict: 'decline' })}
                      >
                        <X className="mr-1 h-3.5 w-3.5" /> Decline
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
        <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="denied-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by person, capability or endpoint…"
              className="pl-8 border-slate-700 bg-slate-900"
            />
          </div>
          <div className="flex gap-1">
            {WINDOWS.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'default' : 'ghost'}
                data-testid={`denied-window-${d}`}
                className={days === d ? 'bg-amber-600 hover:bg-amber-500' : 'text-slate-400'}
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
          <Button
            size="sm"
            variant="ghost"
            data-testid="toggle-deleted"
            className={includeDeleted ? 'text-slate-200' : 'text-slate-500'}
            onClick={() => setIncludeDeleted((v) => !v)}
          >
            {includeDeleted ? 'Hide deleted accounts' : `Show deleted accounts${q.data?.hidden_deleted ? ` (${q.data.hidden_deleted})` : ''}`}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Refusals" value={q.data?.total_attempts ?? '—'} testId="stat-attempts" icon={ShieldAlert} />
          <Stat label="People affected" value={q.data?.people ?? '—'} testId="stat-people" icon={Users} />
          <Stat label="Distinct grants needed" value={q.data?.count ?? '—'} testId="stat-groups" icon={KeyRound} />
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Blocked, and what would unblock it</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {q.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
            {!q.isLoading && items.length === 0 && (
              <p className="p-4 text-center text-xs text-slate-500" data-testid="denied-empty">
                Nothing was refused in this window.
              </p>
            )}
            {items.map((it) => (
              <div
                key={`${it.admin_id}-${it.capability}`}
                data-testid={`denied-row-${it.capability}`}
                className="rounded border border-slate-800 bg-slate-950 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-100">{it.email || 'unknown account'}</span>
                      <Badge className="border-amber-500/40 bg-amber-500/10 font-mono text-[10px] text-amber-200">
                        {it.capability || '—'}
                      </Badge>
                      {it.guard === 'strict' ? (
                        <Badge className="border-rose-500/40 bg-rose-500/10 text-[10px] text-rose-200">
                          <Lock className="mr-0.5 h-2.5 w-2.5" /> strict
                        </Badge>
                      ) : (
                        <Badge className="border-slate-600 bg-slate-800 text-[10px] text-slate-300">baseline</Badge>
                      )}
                      <span className="text-[10px] text-slate-500">
                        {it.attempts} {it.attempts === 1 ? 'refusal' : 'refusals'}
                      </span>
                      {it.account_exists === false && (
                        <Badge className="border-slate-700 bg-slate-900 text-[10px] text-slate-500">
                          account deleted
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                      <Clock className="h-3 w-3" /> last {when(it.last_at)} · first {when(it.first_at)}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(it.endpoints || []).map((e) => (
                        <code key={e} className="rounded bg-slate-900 px-1 py-0.5 text-[10px] text-slate-400">{e}</code>
                      ))}
                      {it.endpoint_count > (it.endpoints || []).length && (
                        <span className="text-[10px] text-slate-500">
                          +{it.endpoint_count - it.endpoints.length} more
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">{it.fix?.note}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    {it.fix?.already_granted ? (
                      <Badge className="border-emerald-500/40 bg-emerald-500/10 text-[10px] text-emerald-200">
                        <Check className="mr-0.5 h-2.5 w-2.5" /> already granted
                      </Badge>
                    ) : it.fix?.grantable ? (
                      <Button
                        size="sm"
                        data-testid={`grant-fix-${it.capability}`}
                        className="bg-emerald-600 hover:bg-emerald-500"
                        disabled={grant.isPending}
                        onClick={() => grant.mutate({ admin_id: it.admin_id, capability: it.capability })}
                      >
                        <KeyRound className="mr-1 h-3.5 w-3.5" /> Grant {it.capability}
                      </Button>
                    ) : (
                      <span className="text-[10px] text-slate-500">{it.fix?.label}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testId, icon: Icon }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3" data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-semibold text-slate-100">{value}</div>
    </div>
  );
}
